import TimerController from "frontend/canvas-designer-new/timer/timer-controller";
import { useTimer } from "frontend/hooks/use-timer";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { useEffect } from "react";
import { TimerButton } from "./timer-button";
import { useAtom } from "jotai";
import { SidepanelType, sidePanelType } from "state-atoms";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { isTimerOpenAtom } from "frontend/app-header/right-header/right-header-atoms";

export function Timer({
  syncService,
  relativeTo,
  isOnline,
  setShowSideMenuOverflow,
  isReadOnly,
  onClick,
}: {
  syncService: SyncService<RW> | null;
  relativeTo: React.RefObject<HTMLElement>;
  isOnline: boolean;
  setShowSideMenuOverflow: (show: boolean) => void;
  isReadOnly: boolean;
  onClick?: () => void;
}) {
  const timer = useTimer(syncService);
  const [showTimer, setShowTimer] = useAtom(isTimerOpenAtom);
  const [openedSidePanel, setOpenedSidePanel] = useAtom(sidePanelType);
  const isCIMReorderEnabled = useIsCIMReorderEnabled();

  useEffect(() => {
    if (timer.config.isRunning && timer.config.pause === 0) {
      if (isCIMReorderEnabled) {
        setOpenedSidePanel(SidepanelType.timer);
      } else {
        setShowTimer(true);
      }
    }
  }, [timer.config.isRunning, timer.config.start, timer.config.pause]);

  useEffect(() => {
    const isTimeOver = timer.config.over;
    if (isCIMReorderEnabled) {
      if (isTimeOver) {
        setOpenedSidePanel(SidepanelType.timer);
      } else {
        setOpenedSidePanel(SidepanelType.none);
      }
    } else {
      setShowTimer(!!isTimeOver);
    }
  }, [timer.config.over]);

  const shouldShowTimer = openedSidePanel == SidepanelType.timer || showTimer;

  return (
    <>
      {shouldShowTimer && (
        <TimerController
          timer={timer}
          showTimer={shouldShowTimer}
          setShowTimer={(open) => {
            if (isCIMReorderEnabled) {
              if (open) {
                setOpenedSidePanel(SidepanelType.timer);
              } else {
                setOpenedSidePanel(SidepanelType.none);
              }
            } else {
              setShowTimer(open);
            }
          }}
          isReadOnly={isReadOnly}
        />
      )}
      {!isReadOnly && (
        <TimerButton
          relativeTo={relativeTo}
          timer={timer}
          isOnline={isOnline}
          showTimer={shouldShowTimer}
          setShowTimer={(open) => {
            if (isCIMReorderEnabled) {
              if (open) {
                setOpenedSidePanel(SidepanelType.timer);
              } else {
                setOpenedSidePanel(SidepanelType.none);
              }
            } else {
              setShowTimer(open);
            }
          }}
          setShowSideMenuOverflow={setShowSideMenuOverflow}
          onClick={onClick}
        />
      )}
    </>
  );
}

export function TimerCIM({ syncService, isReadOnly }: { syncService: SyncService<RW> | null; isReadOnly: boolean }) {
  const timer = useTimer(syncService);
  const [openedSidePanel, setOpenedSidePanel] = useAtom(sidePanelType);

  useEffect(() => {
    if (timer.config.isRunning && timer.config.pause === 0) setOpenedSidePanel(SidepanelType.timer);
  }, [timer.config.isRunning, timer.config.start, timer.config.pause]);

  useEffect(() => {
    timer.config.over ? setOpenedSidePanel(SidepanelType.timer) : setOpenedSidePanel(SidepanelType.none);
  }, [timer.config.over]);

  return openedSidePanel == SidepanelType.timer ? (
    <TimerController
      timer={timer}
      showTimer={openedSidePanel == SidepanelType.timer}
      setShowTimer={(open) => {
        if (open) {
          setOpenedSidePanel(SidepanelType.timer);
        } else {
          setOpenedSidePanel(SidepanelType.none);
        }
      }}
      isReadOnly={isReadOnly}
    />
  ) : null;
}
